import { useState, useEffect, useRef } from "react";
import { LiveWaveform } from "@/components/LiveWaveform";
import { VoicePicker } from "@/components/VoicePicker";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LANGUAGES, ART_STYLES, OUTPUT_OPTIONS } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Clapperboard, Megaphone, BookOpen, Mic, Zap, Check, Loader2, Palette, Languages, AudioLines } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Clapperboard, Megaphone, BookOpen };

const SAMPLE = "Oru chinna ponnu tries to save her village pond that's drying up. Avanga oru magical fish-a meet pannuvanga who grants one wish. Make it emotional but hopeful, with a strong eco message for kids.";

export default function CreateStudio() {
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState("");
  const [language, setLanguage] = useState("English");
  const [customLang, setCustomLang] = useState("");
  const [style, setStyle] = useState("Cinematic 3D Film");
  const [voice, setVoice] = useState("warm");
  const [outputs, setOutputs] = useState(["movie", "campaign", "storybook"]);
  const [listening, setListening] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [detected, setDetected] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const manualLang = useRef(false);

  useEffect(() => {
    if (transcript.trim().length < 12) { setDetected(null); return; }
    const t = setTimeout(async () => {
      setDetecting(true);
      try {
        const res = await api.detectLanguage(transcript);
        setDetected(res);
        if (!manualLang.current) {
          setLanguage(LANGUAGES.includes(res.language) ? res.language : "Custom");
          if (!LANGUAGES.includes(res.language)) setCustomLang(res.language);
        }
      } catch { /* keep last detection */ }
      setDetecting(false);
    }, listening ? 2500 : 1200);
    return () => clearTimeout(t);
  }, [transcript, listening]);

  const pickLanguage = (v) => { manualLang.current = true; setLanguage(v); };

  const toggle = (k) =>
    setOutputs((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const dictate = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input not supported in this browser. Type or paste your idea instead.");
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-IN";
    rec.onstart = () => setListening(true);
    rec.onerror = () => { setListening(false); toast.error("Mic error. Please type your idea."); };
    rec.onend = () => setListening(false);
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      setTranscript(txt);
    };
    rec.start();
    window.__rec = rec;
  };

  const stopDictate = () => { window.__rec?.stop(); setListening(false); };

  const submit = async () => {
    if (!transcript.trim()) return toast.error("Please dictate or type your idea first");
    if (outputs.length === 0) return toast.error("Select at least one pipeline to generate");
    setSubmitting(true);
    try {
      const finalLang = language === "Custom" ? (customLang.trim() || "English") : language;
      const { id } = await api.createProject({
        transcript,
        output_language: finalLang,
        outputs,
        art_style: style,
        voice_style: voice,
      });
      toast.success("Pipelines dispatched! Routing to Command Center…");
      navigate(`/project/${id}`);
    } catch (e) {
      toast.error("Failed to start pipelines");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar showCreate={false} />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 fade-up">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Universal Voice Command Studio</h1>
          <p className="text-[#94A3B8] mt-2">One dictation. Three fully produced creative pipelines.</p>
        </div>

        <div className="glass rounded-2xl p-6 sm:p-8 space-y-8">
          {/* Voice input */}
          <div>
            <Label className="text-sm font-medium text-[#F8FAFC]">
              Speak your movie plot, campaign brief, or story idea via Wispr Flow (any language or mixed speech)
            </Label>
            <div className="relative mt-3">
              <Textarea
                data-testid="voice-transcript-input"
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="e.g. Oru chinna ponnu saves her village pond... / Ek chhoti si kahani... / A detective in neon Tokyo..."
                className="min-h-[160px] bg-[#0F1117]/70 border-white/10 focus:border-[#22C55E] text-base resize-none pr-14 font-serif-i"
              />
              <button
                data-testid="mic-toggle-btn"
                onClick={listening ? stopDictate : dictate}
                className={`absolute top-3 right-3 h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                  listening ? "bg-red-500 text-white pulse-glow" : "bg-[#22C55E] text-[#0F1117] hover:scale-110"
                }`}
                title="Dictate with voice"
              >
                <Mic className="h-5 w-5" />
              </button>
            </div>
            <LiveWaveform active={listening} />
            <div className="flex items-center justify-between mt-2 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs text-[#64748B]">{listening ? "Listening… speak now" : `${transcript.length} characters`}</span>
                {(detecting || detected) && (
                  <span data-testid="detected-language-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#22C55E]/15 text-[#22C55E] px-2.5 py-0.5 text-xs truncate">
                    {detecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                    {detected ? `Detected: ${detected.language}${detected.mix ? ` (${detected.mix})` : ""}` : "Detecting language…"}
                  </span>
                )}
              </div>
              <button data-testid="sample-idea-btn" onClick={() => setTranscript(SAMPLE)} className="text-xs text-[#3B82F6] hover:underline">
                Try a sample idea
              </button>
            </div>
          </div>

          {/* Output pipelines */}
          <div>
            <Label className="text-sm font-medium text-[#F8FAFC]">Target Output — what should we generate?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              {OUTPUT_OPTIONS.map((o) => {
                const Icon = ICONS[o.icon];
                const active = outputs.includes(o.key);
                return (
                  <button
                    key={o.key}
                    data-testid={`output-toggle-${o.key}`}
                    onClick={() => toggle(o.key)}
                    className={`text-left rounded-xl p-4 border transition-all ${
                      active ? "bg-[#1E293B] border-current shadow-lg" : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                    style={{ color: active ? o.accent : undefined }}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className="h-5 w-5" style={{ color: o.accent }} />
                      <span className={`h-5 w-5 rounded-md border flex items-center justify-center ${active ? "border-current" : "border-white/20"}`} style={{ background: active ? o.accent : "transparent" }}>
                        {active && <Check className="h-3.5 w-3.5 text-[#0F1117]" strokeWidth={3} />}
                      </span>
                    </div>
                    <div className="mt-3 text-sm font-semibold text-[#F8FAFC]">{o.label}</div>
                    <div className="text-xs text-[#94A3B8] mt-1 leading-snug">{o.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Language + Style */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-[#F8FAFC]">Output Language</Label>
              <Select value={language} onValueChange={pickLanguage}>
                <SelectTrigger data-testid="language-select" className="mt-3 bg-[#0F1117]/70 border-white/10 h-11">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 max-h-72">
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l} value={l} data-testid={`lang-option-${l}`}>{l}</SelectItem>
                  ))}
                  <SelectItem value="Custom">Custom (type your own)…</SelectItem>
                </SelectContent>
              </Select>
              {language === "Custom" && (
                <Input
                  data-testid="custom-language-input"
                  value={customLang}
                  onChange={(e) => setCustomLang(e.target.value)}
                  placeholder="e.g. Sanskrit, Swahili…"
                  className="mt-2 bg-[#0F1117]/70 border-white/10"
                />
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-[#F8FAFC] flex items-center gap-1.5">
                <Palette className="h-4 w-4 text-[#A855F7]" /> Art & Cinematic Style
              </Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="style-select" className="mt-3 bg-[#0F1117]/70 border-white/10 h-11">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  {ART_STYLES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`style-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(outputs.includes("movie") || outputs.includes("storybook")) && (
            <div>
              <Label className="text-sm font-medium text-[#F8FAFC] flex items-center gap-1.5">
                <AudioLines className="h-4 w-4 text-[#F59E0B]" /> Narrator Voice
                <span className="text-xs font-normal text-[#64748B]">— for film voiceovers & storybook narration (tap ▶ to hear)</span>
              </Label>
              <div className="mt-3"><VoicePicker value={voice} onChange={setVoice} /></div>
            </div>
          )}

          {/* Submit */}
          <Button
            data-testid="execute-pipelines-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full h-14 bg-gradient-to-r from-[#22C55E] to-[#3B82F6] hover:opacity-90 text-white font-bold text-base rounded-xl transition-all disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Dispatching pipelines…</>
            ) : (
              <><Zap className="h-5 w-5 mr-2" /> Execute All Pipelines by Voice</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
